import CloseIcon from "@mui/icons-material/Close";
import { IconButton, InputAdornment } from "@mui/material";
import type { TextFieldProps } from "@mui/material/TextField";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import type { Dayjs } from "dayjs";
import { useState } from "react";

type Props = {
  value: Dayjs | null;
  onChange: (newDate: Dayjs | null) => void;
  ariaLabel: string;
  format: string;
  placeholder: string;
  disabled?: boolean;
  clearAriaLabel?: string;
  onClear?: () => void;
  compact?: boolean;
  minWidth?: number | string;
  textFieldProps?: Partial<TextFieldProps>;
};

export const DeadlinePicker = ({
  value,
  onChange,
  ariaLabel,
  format,
  placeholder,
  disabled = false,
  clearAriaLabel = "Clear due date",
  onClear,
  compact = false,
  minWidth,
  textFieldProps,
}: Props) => {
  const [open, setOpen] = useState(false);
  const compactSx = compact
    ? {
      width: "auto",
      minWidth,
      "& .MuiInput-underline:before": {
        borderBottom: "none",
      },
      "& .MuiInput-underline:after": {
        borderBottom: "none",
      },
      "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
        borderBottom: "none",
      },
      "& .MuiInputBase-root": {
        width: "auto",
        minWidth,
        px: 1,
        py: 0.5,
        borderRadius: 1,
        display: "inline-flex",
        "&:hover": {
          backgroundColor: "action.hover",
        },
      },
      "& .MuiInputBase-input": {
        lineHeight: "20px",
        p: 0,
        width: "auto",
        minWidth: 0,
      },
    }
    : undefined;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        value={value}
        disabled={disabled}
        onChange={(newDate, context) => {
          if (disabled) return;
          if (context.validationError) return;
          setOpen(false);
          onChange(newDate);
        }}
        open={open}
        onClose={() => setOpen(false)}
        format={format}
        slotProps={{
          openPickerButton: {
            sx: { display: "none" },
          },
          textField: {
            size: "small",
            placeholder,
            ...textFieldProps,
            onClick: (event) => {
              if (disabled) {
                textFieldProps?.onClick?.(event);
                return;
              }
              setOpen(true);
              textFieldProps?.onClick?.(event);
            },
            inputProps: {
              ...(textFieldProps?.inputProps ?? {}),
              "aria-label": ariaLabel,
            },
            sx: textFieldProps?.sx ?? compactSx,
            InputProps: {
              ...(textFieldProps?.InputProps ?? {}),
              endAdornment:
                value && onClear && !disabled ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label={clearAriaLabel}
                      onClick={(event) => {
                        event.stopPropagation();
                        setOpen(false);
                        onClear();
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : (
                  textFieldProps?.InputProps?.endAdornment ?? null
                ),
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};
